import { useEffect, useState } from 'react';
import { ArrowClockwise, CheckCircle, Circle, CircleNotch, WarningCircle } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BootstrapJob } from '../hooks/use-bootstrap-job';

type StepState = 'done' | 'active' | 'pending' | 'failed';

interface Step {
  key: string;
  label: string;
  detail: string | null;
  state: StepState;
}

const pad = (n: number) => String(n).padStart(2, '0');

export const elapsedLabel = (seconds: number): string =>
  seconds >= 60 ? `${Math.floor(seconds / 60)} min ${pad(seconds % 60)} s` : `${seconds} s`;

const plural = (n: number, one: string, many: string) => (n > 1 ? many : one);

/* Les étapes SONT l'indicateur. Aucune n'avance toute seule : chacune reflète
   un fait écrit en base par le travail lui-même. La seule barre affichée est
   celle de l'enregistrement, adossée à un décompte de sections réellement
   créées — la rédaction, elle, est un unique appel au modèle dont personne ne
   connaît l'avancement, et lui inventer une barre serait mentir. */
export function bootstrapSteps(job: BootstrapJob): Step[] {
  const failed = job.status === 'failed';
  const planned = `${job.sections_total} ${plural(job.sections_total, 'section demandée', 'sections demandées')}`;
  const saving = `${job.sections_created} / ${job.sections_total} ${plural(job.sections_created, 'section enregistrée', 'sections enregistrées')}`;

  const generating: StepState =
    job.step === 'generating' ? (failed ? 'failed' : 'active') : 'done';
  const persisting: StepState =
    job.step === 'generating'
      ? 'pending'
      : job.step === 'saving'
        ? (failed ? 'failed' : 'active')
        : failed ? 'failed' : 'done';

  return [
    { key: 'plan', label: 'Plan validé et travail enregistré', detail: planned, state: 'done' },
    {
      key: 'generating',
      label: 'Rédaction du cours par le modèle',
      detail: generating === 'active' ? `environ ${job.typical_duration_seconds} s en général` : null,
      state: generating,
    },
    {
      key: 'saving',
      label: 'Enregistrement des sections',
      detail: job.step === 'generating' ? null : saving,
      state: persisting,
    },
  ];
}

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') return <CheckCircle size={18} weight="fill" className="text-[var(--naia-green)] shrink-0" />;
  if (state === 'failed') return <WarningCircle size={18} weight="fill" className="text-[var(--naia-error)] shrink-0" />;
  if (state === 'active') return <CircleNotch size={18} className="text-naia-blue shrink-0 animate-spin" />;
  return <Circle size={18} className="text-[var(--naia-text-secondary)] opacity-40 shrink-0" />;
}

function useElapsedSeconds(startedAt: string, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [running]);
  return Math.max(0, Math.round((now - Date.parse(startedAt)) / 1000));
}

interface Props {
  job: BootstrapJob;
  showSuccess: boolean;
  onRetry: () => void;
  retrying: boolean;
  retryError: string | null;
}

export default function BootstrapProgress({ job, showSuccess, onRetry, retrying, retryError }: Props) {
  const running = job.status === 'running';
  const elapsed = useElapsedSeconds(job.created_at, running);

  if (job.status === 'succeeded') {
    if (!showSuccess) return null;
    return (
      <div
        role="status"
        className="mb-5 flex items-center gap-2 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-green-bg)] p-3 text-[13px] text-[var(--naia-text-primary)]"
      >
        <CheckCircle size={18} weight="fill" className="text-[var(--naia-green)] shrink-0" />
        <span>
          Cours généré — {job.sections_created} {plural(job.sections_created, 'section ajoutée', 'sections ajoutées')}.
        </span>
      </div>
    );
  }

  return (
    <section
      aria-live="polite"
      className="mb-5 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h2 className="m-0 text-[14px] font-bold text-[var(--naia-text-primary)]">
          {running ? 'Rédaction du cours en cours' : 'La rédaction du cours a échoué'}
        </h2>
        {running && (
          <span className="ml-auto font-mono text-[12px] tabular-nums text-[var(--naia-text-secondary)]">
            {elapsedLabel(elapsed)}
          </span>
        )}
      </header>

      <ol className="mt-3 mb-0 list-none space-y-2 p-0">
        {bootstrapSteps(job).map((step) => (
          <li key={step.key} className="flex items-start gap-2">
            <StepIcon state={step.state} />
            <div className="min-w-0">
              <p
                className={cn(
                  'm-0 text-[13px] leading-[1.4]',
                  step.state === 'pending'
                    ? 'text-[var(--naia-text-secondary)]'
                    : 'font-semibold text-[var(--naia-text-primary)]',
                )}
              >
                {step.label}
              </p>
              {step.detail && (
                <p className="m-0 text-[12px] leading-[1.4] text-[var(--naia-text-secondary)]">{step.detail}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {running && job.step === 'saving' && job.sections_total > 0 && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={job.sections_total}
          aria-valuenow={job.sections_created}
          aria-label="Sections enregistrées"
          className="mt-3 h-[6px] w-full overflow-hidden rounded-full bg-[var(--naia-line)]"
        >
          <div
            className="h-full rounded-full bg-naia-blue transition-[width] duration-300"
            style={{ width: `${Math.round((job.sections_created / job.sections_total) * 100)}%` }}
          />
        </div>
      )}

      {running && (
        <p className="mt-3 mb-0 text-[12px] leading-[1.45] text-[var(--naia-text-secondary)]">
          Vous pouvez fermer cet onglet ou travailler ailleurs : la rédaction continue sur nos serveurs et vous la
          retrouverez ici.
        </p>
      )}

      {job.status === 'failed' && (
        <>
          <p className="mt-3 mb-0 rounded-[var(--naia-r-sm)] bg-[var(--naia-error-bg)] p-2 text-[12px] leading-[1.45] text-[var(--naia-text-primary)]">
            {job.error ?? 'La génération s\'est arrêtée sans message.'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" className="min-h-11 px-4" disabled={retrying} onClick={onRetry}>
              {retrying ? <CircleNotch size={15} className="animate-spin" /> : <ArrowClockwise size={15} />}
              {retrying ? 'Relance…' : 'Relancer la génération'}
            </Button>
            <span className="text-[12px] text-[var(--naia-text-secondary)]">
              Le plan validé est conservé — rien à ressaisir.
            </span>
          </div>
          {retryError && (
            <p className="mt-2 mb-0 text-[12px] text-[var(--naia-error)]">{retryError}</p>
          )}
        </>
      )}
    </section>
  );
}
